import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useContext, useState, useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOSegment from '@atlaskit/react-ufo/segment';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Box, xcss } from '@atlaskit/primitives';
import { useIsFhsEnabled } from '@atlaskit/navigation-system/fhs-rollout';
import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';
import {
	SideNav,
	SideNavFooter,
	SideNavPanelSplitter,
	useExpandSideNav,
	useToggleSideNav,
} from '@atlaskit/navigation-system/layout/side-nav';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';

import { GlobalInviteWithLazyLoadedRelayProvider } from '@atlassian/global-invite';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { componentWithCondition } from '@confluence/feature-gating-utils';
import { type WithFlagsProps, withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useSettingsBackButtonUtils } from '@confluence/admin-side-navigation';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { AdminNavigationLoader } from '@confluence/side-navigation/entry-points/AdminNavigation.ts';
import { StarredSpacesList } from '@confluence/side-navigation/entry-points/StarredSpacesList.ts';
import { HubSettingsNavigation } from '@confluence/side-navigation/entry-points/HubSettingsNavigation.ts';
import { SpaceAdminNavigation } from '@confluence/side-navigation/entry-points/SpaceAdminNavigation.ts';
import { GlobalNavigation } from '@confluence/side-navigation/entry-points/GlobalNavigation.ts';
import {
	SpaceNavigation,
	SpaceNavigationLoadingSkeleton,
} from '@confluence/side-navigation/entry-points/SpaceNavigation.ts';
import { isDialogShown } from '@confluence/dialogs/entry-points/helpers.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { ShortcutListener } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';
import {
	SIDE_NAVIGATION_SHORTCUT,
	UNIVERSAL_SIDE_NAVIGATION_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { WHITEBOARD_IFRAME_TITLE } from '@confluence/whiteboard-utils/entry-points/whiteboardConstants.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { AnonymousAccessSideNavMessage } from '@confluence/anonymous-access/entry-points/AnonymousAccessSideNavMessage.ts';
import { useIsUserViewingSpaceAsAnon } from '@confluence/anonymous-access/entry-points/useIsUserViewingSpaceAsAnon.ts';
import { usePersistentInviteButtonEligible } from '@confluence/persistent-invite-button/entry-points/usePersistentInviteButtonEligible.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { PostOfficeConfluenceSideNavPlacement } from '@confluence/experiment-post-office-side-nav';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { useModernizeChangeboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useModernizeChangeboardingActive.ts';
import { useModernizeChangeboardingTapTraits } from '@confluence/modernize-changeboarding/entry-points/useModernizeChangeboardingTapTraits.ts';
import { useGetModernizeChangeboardingOnboardingState } from '@confluence/modernize-changeboarding/entry-points/useGetModernizeChangeboardingOnboardingState.ts';
import {
	Nav4CollapseReminderSpotlightLoader,
	useHasDismissedNav4CollapseReminderSpotlight,
} from '@confluence/nav4-onboarding/entry-points/Nav4CollapseReminderSpotlightLoader.ts';
import { maybeShowDeprecatedShortcutFlag } from '@confluence/shortcuts/entry-points/DeprecatedShortcutFlag.tsx';
import { isBlogPage } from '@confluence/named-routes/entry-points/routes-utils.ts';

import { SideNavigationContent } from './SideNavigationContent.tsx';
import { useSideNavInitialState } from './useSideNavInitialState.ts';
import { useScrollTree } from './useScrollTree.ts';
import {
	ADMIN_HOME_ID,
	CONTAINER_ADMIN_HOME_ID,
	CONTAINER_HOME_ID,
	COMPANY_HUB_SETTINGS_ID,
	CONTAINER_HOME_LOADING_ID,
} from './paths.ts';
import {
	useSideNavigationCollapsedActions,
	useSideNavigationCollapsedState,
} from './SideNavigationCollapsedStore.ts';
import { PlatformNavigation } from '@confluence/side-navigation/entry-points/PlatformNavigation.ts';

const i18n = defineMessages({
	dragHandle: {
		id: 'page-layout.side-navigation.drag-handle',
		description:
			'A label to tell visually impaired users that the selected element is the side navigation drag handle for resizing the sidebar',
		defaultMessage: 'Side Navigation Drag Handle',
	},
	sideNavPanelSplitterTooltipContent: {
		id: 'page-layout.side-navigation.side-nav-panel-splitter-tooltip-content',
		description:
			'Text content of the tooltip that appears when hovering over the side nav panel splitter, telling users that double clicking on the element will collapse the side nav',
		defaultMessage: 'Double click to collapse',
	},
	globalInviteButtonLabel: {
		id: 'page-layout.side-navigation.global-invite-button-label',
		description: 'Text content of the button that triggers the modal dialog.',
		defaultMessage: 'Invite people',
	},
});

const resize = () => {
	window.dispatchEvent(new Event('resize'));
};

const ExpandKeyboardShortcut = ({ onToggleFromShortcut }: { onToggleFromShortcut: () => void }) => {
	const isOnEditRoute = useIsEditorPage();
	const toggleSideNav = useToggleSideNav();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

	const toggle = useCallback(() => {
		if (!isDialogShown()) {
			onToggleFromShortcut();
			toggleSideNav();
		}
	}, [onToggleFromShortcut, toggleSideNav]);

	return isLiveEditMode || isOnEditRoute ? (
		<ShortcutListener accelerator={UNIVERSAL_SIDE_NAVIGATION_SHORTCUT} listener={toggle} />
	) : (
		<GeneralShortcutListener
			accelerator={UNIVERSAL_SIDE_NAVIGATION_SHORTCUT}
			deprecatedAccelerator={SIDE_NAVIGATION_SHORTCUT}
			listener={toggle}
		/>
	);
};

/**
 * Used when full height sidebar (FHS) is enabled.
 * Uses the `Ctrl+[` shortcut that is built into the ADS navigation system, instead of Confluence's
 * own `Ctrl+[` shortcut (UNIVERSAL_SIDE_NAVIGATION_SHORTCUT).
 * But, we keep Confluence's `[` shortcut (SIDE_NAVIGATION_SHORTCUT) on non-edit routes, and display
 * an educational flag pointing to the new shortcut.
 */
const ExpandKeyboardShortcutFHS = withFlags(
	({ flags, onToggleFromShortcut }: WithFlagsProps & { onToggleFromShortcut: () => void }) => {
		const isOnEditRoute = useIsEditorPage();
		const toggleSideNav = useToggleSideNav();
		const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

		const toggle = useCallback(() => {
			if (!isDialogShown()) {
				onToggleFromShortcut();
				toggleSideNav();

				// Show the educational flag for the `Ctrl+[` shortcut, when the `[` shortcut is used.
				maybeShowDeprecatedShortcutFlag({
					flags,
					acceleratorTriggered: SIDE_NAVIGATION_SHORTCUT,
					accelerator: UNIVERSAL_SIDE_NAVIGATION_SHORTCUT,
					deprecatedAccelerator: SIDE_NAVIGATION_SHORTCUT,
				});
			}
		}, [flags, onToggleFromShortcut, toggleSideNav]);

		if (isLiveEditMode || isOnEditRoute) {
			// Edit routes will still have the side nav shortcut that is built into the ADS SideNav component.
			return null;
		}

		// Non-edit routes still have the `[` shortcut, which will eventually be deprecated.
		// An educational flag is shown during the transition process.
		return <GeneralShortcutListener accelerator={SIDE_NAVIGATION_SHORTCUT} listener={toggle} />;
	},
);

const ExpandKeyboardShortcutWithFG = componentWithCondition(
	() =>
		fg('navx-2733-convert-confluence-fhs-rollout')
			? useIsFhsEnabled()
			: fg('navx-full-height-sidebar'),
	ExpandKeyboardShortcutFHS,
	ExpandKeyboardShortcut,
);

// When full height sidebar is enabled, the SideNavPanelSplitter component should be used.
// It supports double clicking to collapse the side nav.
const PanelSplitterWithFG = componentWithCondition(
	() =>
		fg('navx-2733-convert-confluence-fhs-rollout')
			? useIsFhsEnabled()
			: fg('navx-full-height-sidebar'),
	SideNavPanelSplitter,
	PanelSplitter,
);

const useIsModeGlobal = () => {
	const { match } = useContext(RoutesContext);

	const [spaceKey] = usePageSpaceKey();

	return match?.query?.mode === 'global' || isCompanyHubSpaceKey(spaceKey);
};

//todo: are all these views still relevant? I'm not certain they are
const getProductSideNavigation = (
	view: string | undefined,
	isSpaceSettingsScreen: boolean,
	isModeGlobal: boolean,
	isOnAnySettingsScreen: boolean,
	unhandledError: ApolloErrorInterop | undefined,
	isLicensed: boolean,
) => {
	// hide product nav if mode=global unless we're on a settings page
	if (isModeGlobal && !isOnAnySettingsScreen) {
		return null;
	}

	switch (view) {
		case CONTAINER_HOME_LOADING_ID:
			return <SpaceNavigationLoadingSkeleton />;
		case CONTAINER_ADMIN_HOME_ID:
			return <SpaceAdminNavigation />;
		case COMPANY_HUB_SETTINGS_ID:
			return <HubSettingsNavigation />;
		case ADMIN_HOME_ID:
			return <AdminNavigationLoader />;
		case CONTAINER_HOME_ID:
			return (
				<SpaceNavigation
					isSpaceSettingsScreen={isSpaceSettingsScreen}
					{...(unhandledError && { unhandledError })}
					isBlogNavigation={isBlogPage()}
				/>
			);
		default:
			return isLicensed ? <StarredSpacesList /> : null;
	}
};

export type SideNavProps = {
	view: string | undefined;
	isSpaceSettingsScreen: boolean;
	skiplinkText?: string;
	isNav4CollapseReminderSpotlightActive?: boolean;
	setIsNav4CollapseReminderSpotlightActive?: Dispatch<SetStateAction<boolean>>;
};

export const SideNavigationInner = ({
	view,
	isSpaceSettingsScreen,
	isNav4CollapseReminderSpotlightActive,
	setIsNav4CollapseReminderSpotlightActive,
}: SideNavProps) => {
	const isFhsEnabled = fg('navx-2733-convert-confluence-fhs-rollout')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsFhsEnabled()
		: fg('navx-full-height-sidebar');

	const intl = useIntl();
	const isModeGlobal = useIsModeGlobal(); //instead of disabling the whole left nav, now mode=Global will disable only the space Navigation
	const { initialCollapsedState, initialWidth, shouldForceExpand } = useSideNavInitialState();
	const { scrollTree } = useScrollTree({
		view,
	});
	const { saveCurrentRoute } = useSettingsBackButtonUtils();
	const [spaceKey] = usePageSpaceKey();
	const { isLicensed } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isOnEditRoute = useIsEditorPage();
	const isPageWithinSpaceSettings = view === CONTAINER_ADMIN_HOME_ID;
	const isPageWithinSiteSettings = view === ADMIN_HOME_ID;
	const isHubSettingsScreen = view === COMPANY_HUB_SETTINGS_ID;
	const isOnAnySettingsScreen =
		isPageWithinSpaceSettings || isPageWithinSiteSettings || isHubSettingsScreen;
	const { nav4GlobalChangeboardingActive, nav4SpaceSettingsChangeboardingActive } =
		useModernizeChangeboardingState();
	const expandSideNav = useExpandSideNav();
	const pressTracing = usePressTracing('side-nav-collapse-reminder-spotlight');
	const {
		isUserViewingSpaceAsAnon,
		loading: anonymousAccessQueryLoading,
		error: anonymousAccessQueryError,
	} = useIsUserViewingSpaceAsAnon({ spaceKey: spaceKey || '' });
	const [{ isCollapsed }] = useSideNavigationCollapsedState();
	const { setIsCollapsed } = useSideNavigationCollapsedActions();

	const productSideNavigation = getProductSideNavigation(
		view,
		isSpaceSettingsScreen,
		isModeGlobal,
		isOnAnySettingsScreen,
		anonymousAccessQueryError,
		isLicensed,
	);

	const [triggeredByShortcut, setTriggeredByShortcut] = useState(false);

	const fireSidebarToggleAnalytics = useCallback(
		(action: 'collapsed' | 'expanded', triggeredBy: 'keyboardShortcut' | 'click') => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject: 'spaceNavigation',
					source: 'containerNavigation',
					attributes: {
						isOnEditRoute,
						navigationLayer: 'global',
						navigationContainer: 'top',
						navVersion: '4',
						triggeredBy,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isOnEditRoute],
	);

	const fireSidebarResizeEndAnalytics = useCallback(
		({ initialWidth, finalWidth }: { initialWidth: number; finalWidth: number }) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'resized',
					actionSubject: 'spaceNavigation',
					source: 'containerNavigation',
					attributes: {
						navigationLayer: 'global',
						navigationContainer: 'top',
						navVersion: '4',
						initialWidth,
						finalWidth,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const onCollapse = useCallback(() => {
		setIsCollapsed(true);
		confluenceLocalStorageInstance.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_COLLAPSED,
			'collapsed',
		);

		resize();
		fireSidebarToggleAnalytics('collapsed', triggeredByShortcut ? 'keyboardShortcut' : 'click');
		setTriggeredByShortcut(false);
	}, [fireSidebarToggleAnalytics, triggeredByShortcut, setIsCollapsed]);

	const onExpand = useCallback(() => {
		setIsCollapsed(false);
		confluenceLocalStorageInstance.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_COLLAPSED,
			'expanded',
		);
		scrollTree();
		resize();
		fireSidebarToggleAnalytics('expanded', triggeredByShortcut ? 'keyboardShortcut' : 'click');
		setTriggeredByShortcut(false);
	}, [scrollTree, fireSidebarToggleAnalytics, triggeredByShortcut, setIsCollapsed]);

	const handleToggleFromShortcut = useCallback(() => {
		setTriggeredByShortcut(true);
	}, []);

	// If current route is not a settings page, save the current route to session storage.
	// The saved route is read by the back button whenever the user lands in Site or Space settings in the same session.
	if (!isPageWithinSpaceSettings && !isPageWithinSiteSettings && !isHubSettingsScreen) {
		saveCurrentRoute();
	}

	const { isNav4CollapseReminderSpotlightDismissed } =
		useHasDismissedNav4CollapseReminderSpotlight();
	const { isNav4SpaceTourDismissed } = useGetModernizeChangeboardingOnboardingState();
	const { hasDismissedNav4ChangeboardingViaEP } = useModernizeChangeboardingTapTraits();

	const isNav4ChangeboardingDismissed =
		isNav4SpaceTourDismissed || hasDismissedNav4ChangeboardingViaEP;

	useEffect(() => {
		// Expand side nav when rendering certain nav4 spotlights
		if (
			(nav4GlobalChangeboardingActive || nav4SpaceSettingsChangeboardingActive) &&
			fg('cc_modernize_ga_changeboarding')
		) {
			expandSideNav();
		}
	}, [nav4GlobalChangeboardingActive, nav4SpaceSettingsChangeboardingActive, expandSideNav]);

	const canToggleWithShortcut = useCallback(() => {
		// Block the side nav toggle keyboard shortcut when a modal is open
		return !isDialogShown();
	}, []);

	return (
		<SideNav
			defaultWidth={initialWidth}
			defaultCollapsed={isFhsEnabled ? undefined : initialCollapsedState === 'collapsed'}
			onExpand={onExpand}
			onCollapse={onCollapse}
			id="side-navigation"
			canToggleWithShortcut={canToggleWithShortcut}
		>
			<UFOSegment name="side-nav">
				<SideNavigationContent>
					<ErrorBoundary
						attribution={Attribution.DISCO}
						attributes={{
							errorBoundaryId: 'sideNavigation-globalNavigation',
						}}
					>
						<GlobalNavigation
							productSideNavigation={productSideNavigation}
							isOnAnySettingsScreen={isOnAnySettingsScreen}
							isCollapsed={isCollapsed}
						/>
					</ErrorBoundary>
					{isNav4CollapseReminderSpotlightActive && setIsNav4CollapseReminderSpotlightActive && (
						<Nav4CollapseReminderSpotlightLoader
							setIsNav4CollapseReminderSpotlightActive={setIsNav4CollapseReminderSpotlightActive}
						/>
					)}
					{!shouldForceExpand && (
						<ExpandKeyboardShortcutWithFG onToggleFromShortcut={handleToggleFromShortcut} />
					)}
					<PanelSplitterWithFG
						onResizeEnd={({ initialWidth, finalWidth }) => {
							handleResizeEnd({
								finalWidth,
								eligibleForCollapseReminderSpotlight:
									!isNav4CollapseReminderSpotlightActive &&
									isNav4ChangeboardingDismissed &&
									!isNav4CollapseReminderSpotlightDismissed,
								setIsNav4CollapseReminderSpotlightActive,
								pressTracing,
							});

							fireSidebarResizeEndAnalytics({
								initialWidth,
								finalWidth,
							});
						}}
						onResizeStart={onResizeStart}
						label={intl.formatMessage(i18n.dragHandle)}
						testId="side-navigation-panel-splitter"
						tooltipContent={
							isFhsEnabled ? intl.formatMessage(i18n.sideNavPanelSplitterTooltipContent) : undefined
						}
					/>
				</SideNavigationContent>
				{isUserViewingSpaceAsAnon ? (
					<AnonymousAccessSideNavMessage />
				) : (
					!anonymousAccessQueryLoading && !anonymousAccessQueryError && <SideNavFooterWrapped />
				)}
			</UFOSegment>
		</SideNav>
	);
};

const SideNavigationSkeleton = () => {
	const isFhsEnabled = fg('navx-2733-convert-confluence-fhs-rollout')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsFhsEnabled()
		: fg('navx-full-height-sidebar');

	const { initialCollapsedState, initialWidth } = useSideNavInitialState();

	return (
		<UFOLoadHold
			hold={expVal('confluence_frontend_side_nav_ufohold', 'isEnabled', false)}
			name="side-navigation-skeleton"
		>
			<SideNav
				defaultWidth={initialWidth}
				defaultCollapsed={isFhsEnabled ? undefined : initialCollapsedState === 'collapsed'}
				id="side-navigation-skeleton"
			>
				<UFOSegment name="side-nav-skeleton">
					<SideNavigationContent>
						<SpaceNavigationLoadingSkeleton />
					</SideNavigationContent>
				</UFOSegment>
			</SideNav>
		</UFOLoadHold>
	);
};

export const SideNavigation = (props: SideNavProps) => {
	return (
		<SuspenseWithDataFetcher
			dataFetcherName="side-navigation"
			fallback={<SideNavigationSkeleton />}
		>
			{fg('navx-global-side-nav-confluence') ? (
				<PlatformNavigation />
			) : (
				<SideNavigationInner {...props} />
			)}
		</SuspenseWithDataFetcher>
	);
};

const getSideNavContent = () => {
	if (!fg('post-office-connie-side-nav-killswitch')) {
		return null;
	}
	return <PostOfficeConfluenceSideNavPlacement nav4 />;
};

const SideNavFooterWrapped = () => {
	return (
		<>
			{getSideNavContent()}
			<SideNavFooterContent />
		</>
	);
};

const globalInviteButtonStyles = xcss({
	flexGrow: 1,
});

const Nav4SideNavFooterContent = withFlags(({ flags }: WithFlagsProps) => {
	const intl = useIntl();

	const experimentalFeatures = useMemo(
		() => ({
			showConfluenceCopyLink: expVal(
				'jfind-copy-link-modal-experiment-confluence',
				'enrolled',
				false,
			),
		}),
		[],
	);
	return (
		<Inline space="space.100" testId="nav4-side-nav-footer-content">
			<Box xcss={globalInviteButtonStyles}>
				<GlobalInviteWithLazyLoadedRelayProvider
					product="confluence"
					showFlag={flags.showFlag}
					experimentalFeatures={experimentalFeatures}
				>
					{intl.formatMessage(i18n.globalInviteButtonLabel)}
				</GlobalInviteWithLazyLoadedRelayProvider>
			</Box>
		</Inline>
	);
});

const SideNavFooterContent = () => {
	const { error, loading, isEligible } = usePersistentInviteButtonEligible();
	if (loading) return null;

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		} else {
			return <ErrorDisplay error={error} />;
		}
	}

	return (
		isEligible &&
		fg('confluence_nav4_global-invite') && (
			<SideNavFooter>
				<TransparentErrorBoundary attribution={Attribution.NAVX}>
					<Nav4SideNavFooterContent />
				</TransparentErrorBoundary>
			</SideNavFooter>
		)
	);
};

// CCT-951 TEMPORARY WORKAROUND to allow resizing of side-nav when viewing a Whiteboard (rendered in an iFrame)
// Can be removed when DSP-5931 is fixed and @atlaskit/page-layout is bumped
const onResizeStart = () => {
	(
		document.querySelector(`iframe[title="${WHITEBOARD_IFRAME_TITLE}"]`) as HTMLElement
	)?.style.setProperty('pointer-events', 'none');
};

const handleResizeEnd = ({
	finalWidth,
	eligibleForCollapseReminderSpotlight,
	setIsNav4CollapseReminderSpotlightActive,
	pressTracing,
}: {
	finalWidth: number;
	eligibleForCollapseReminderSpotlight: boolean;
	setIsNav4CollapseReminderSpotlightActive?: Dispatch<SetStateAction<boolean>>;
	pressTracing: () => void;
}) => {
	// CCT-951 TEMPORARY WORKAROUND to allow resizing of side-nav when viewing a Whiteboard (rendered in an iFrame)
	// Can be removed when DSP-5931 is fixed and @atlaskit/page-layout is bumped
	(
		document.querySelector(`iframe[title="${WHITEBOARD_IFRAME_TITLE}"]`) as HTMLElement
	)?.style.removeProperty('pointer-events');

	confluenceLocalStorageInstance.setItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_WIDTH,
		finalWidth,
	);

	if (eligibleForCollapseReminderSpotlight) {
		if (expVal('confluence_frontend_nav_spotlight_press_tracing', 'isEnabled', false)) {
			pressTracing();
		}
		setIsNav4CollapseReminderSpotlightActive?.(true);
	}
};
